/**
 * Issuer Hub Access Gate Hook
 *
 * P2-05: Resolves whether the current user may view or interact with the
 * Issuer Hub, and surfaces their access level for conditional UI rendering.
 *
 * Resolution order:
 *  1. Admin users bypass permission checks. If they have an issuing_bank entry
 *     in entityAccessList, use that corporate ID directly. If not (common for
 *     platform admins without an explicit access row), fall back to querying
 *     corporates for the first issuer_hub_enabled=true record.
 *  2. For non-admin users, filter entityAccessList from useCorporateAccess() to
 *     find the entry with entityType === 'issuing_bank'.
 *  3. Use the found corporateId to query the corporates table for issuer_hub_enabled.
 *  4. Derive hasAccess and canWrite from the enabled flag and accessLevel.
 *
 * The `loading` flag remains true until BOTH the entity access list and the
 * corporates query have resolved — prevents flashing unauthorized content.
 *
 * React Query cache: ['issuer-hub-access', userId], staleTime: 5 min.
 * Admin fallback cache: ['issuer-hub-admin-fallback'], staleTime: 5 min.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCorporateAccess } from "@/hooks/useCorporateAccess";
import type { IssuerHubAccessState } from "@/types/IssuerPipeline";

// ---------------------------------------------------------------------------
// Closed-over fallback — returned while loading or when access is denied.
// ---------------------------------------------------------------------------

const LOADING_STATE: IssuerHubAccessState = {
  hasAccess: false,
  canWrite: false,
  isEnabled: false,
  issuerCorporateId: null,
  accessLevel: null,
  loading: true,
};

const NO_ACCESS_STATE: IssuerHubAccessState = {
  hasAccess: false,
  canWrite: false,
  isEnabled: false,
  issuerCorporateId: null,
  accessLevel: null,
  loading: false,
};

// ---------------------------------------------------------------------------
// Shape of the corporates row subset we need (issuer_hub_enabled is a J.1
// migration column not yet reflected in the generated types).
// ---------------------------------------------------------------------------

interface CorporateHubRow {
  issuer_hub_enabled: boolean;
}

interface CorporateIdRow {
  id: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Resolves issuer hub access state for the current authenticated user.
 *
 * Returns `IssuerHubAccessState`. The `loading` flag is `true` until all
 * async checks have resolved — consumers should gate UI on this flag to
 * avoid flashing unauthorized content before permissions are confirmed.
 */
export function useIssuerHubAccess(): IssuerHubAccessState {
  const { user, hasRole, loading: authLoading } = useAuth();
  const { entityAccessList, loading: accessLoading } = useCorporateAccess();

  const isAdmin = hasRole("admin");

  // Find the issuing_bank entry in the entity access list.
  // For non-admin users this is populated from user_corporate_access rows.
  // For admin users, useCorporateAccess skips the DB query entirely (returns []),
  // so entityAccessList is always empty for admins — hence the fallback below.
  const issuerEntry = entityAccessList.find((e) => e.entityType === "issuing_bank");
  const issuerCorporateId = issuerEntry?.corporateId ?? null;
  const accessLevel = issuerEntry?.accessLevel ?? null;

  // ── Admin fallback query ─────────────────────────────────────────────────
  // Admins skip user_corporate_access lookups, so issuerCorporateId is null
  // for platform admins without an explicit issuing_bank access row.
  // When that happens, query corporates directly for any issuer_hub_enabled
  // record and use the first result as the admin's issuer corporate.
  const {
    data: adminFallbackRow,
    isLoading: adminFallbackLoading,
  } = useQuery({
    queryKey: ["issuer-hub-admin-fallback"],
    queryFn: async (): Promise<CorporateIdRow | null> => {
      const { data, error } = await supabase
        .from("corporates")
        .select("id")
        .eq("issuer_hub_enabled", true)
        .order("id")
        .limit(1)
        .single() as { data: CorporateIdRow | null; error: unknown };

      if (error) return null;
      return data;
    },
    enabled: isAdmin && !authLoading && issuerCorporateId === null,
    staleTime: 5 * 60 * 1000,
  });

  // Query the corporates table for the issuer_hub_enabled flag.
  // Only runs when:
  //   - We are not admin (admins bypass all checks)
  //   - Auth + entity access have resolved
  //   - We found an issuing_bank entry with a corporate ID
  const {
    data: corporateRow,
    isLoading: corporateLoading,
  } = useQuery({
    queryKey: ["issuer-hub-access", user?.id],
    queryFn: async (): Promise<CorporateHubRow | null> => {
      if (!issuerCorporateId) return null;

      const { data, error } = await supabase
        .from("corporates")
        .select("issuer_hub_enabled")
        .eq("id", issuerCorporateId)
        .single() as { data: CorporateHubRow | null; error: unknown };

      if (error) {
        // Non-fatal — treat as disabled rather than crashing the gate
        return null;
      }

      return data;
    },
    enabled: !isAdmin && !authLoading && !accessLoading && issuerCorporateId !== null,
    staleTime: 5 * 60 * 1000,
  });

  // ── Admin shortcut ──────────────────────────────────────────────────────
  // Admins bypass permission checks. Wait for the fallback query to resolve
  // when no issuing_bank entry was found in the access list.
  if (isAdmin && !authLoading) {
    // Still waiting for the fallback corporate lookup
    if (issuerCorporateId === null && adminFallbackLoading) {
      return LOADING_STATE;
    }

    const resolvedId = issuerCorporateId ?? adminFallbackRow?.id ?? null;

    return {
      hasAccess: resolvedId !== null,
      canWrite: resolvedId !== null,
      isEnabled: resolvedId !== null,
      issuerCorporateId: resolvedId,
      accessLevel: accessLevel ?? (resolvedId !== null ? "owner" : null),
      loading: false,
    };
  }

  // ── Loading guard ────────────────────────────────────────────────────────
  // Keep loading=true until every async dependency has settled.
  const isLoading =
    authLoading ||
    accessLoading ||
    // If we expect a corporates query (issuerCorporateId is set), wait for it.
    (issuerCorporateId !== null && corporateLoading);

  if (isLoading) {
    return LOADING_STATE;
  }

  // ── No issuing_bank entry ───────────────────────────────────────────────
  if (!issuerCorporateId) {
    return NO_ACCESS_STATE;
  }

  // ── Derive access from DB flag + access level ───────────────────────────
  const isEnabled = corporateRow?.issuer_hub_enabled === true;
  const hasAccess = isEnabled;
  const canWrite =
    isEnabled && (accessLevel === "owner" || accessLevel === "editor");

  return {
    hasAccess,
    canWrite,
    isEnabled,
    issuerCorporateId,
    accessLevel,
    loading: false,
  };
}
